import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import AuthServ from "../service/AuthServ";
import Navbar from "./Navbar";
import GlobalButton from "../util/GlobalButton";
import Notify from "../util/Notify";
import "../css/startSelling.css";
import {
  IconRocket,
  IconArrowRight,
  IconClockPlay,
  IconShieldCheck,
  IconBuildingStore,
  IconChartBar,
  IconDeviceMobileMessage,
  IconCheck,
  IconTruckDelivery,
  IconWallet
} from "@tabler/icons-react";

const StartSelling = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        setIsLoggedIn(true);
        // Sync role from backend
        try {
          const res = await AuthServ.single();
          if (res?.data) {
            const role = res.data.role?.toLowerCase();
            setUserRole(role);
            localStorage.setItem("role", role);

            if (role === "seller") {
              navigate("/seller/dashboard");
            }
          }
        } catch (err) {
          console.error("Role sync failed in StartSelling", err);
          setUserRole(localStorage.getItem("role")?.toLowerCase());
        }
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Handle Apply as Existing User
  const handleApplyNow = async () => {
    if (userRole === "seller") {
      Notify.warning("Already Verified", "You are already verified as a seller.");
      navigate("/seller/dashboard");
      return;
    }

    if (userRole === "pendingseller") {
      Notify.info("Pending Application", "Your seller application is already under review.");
      return;
    }

    // Regular user - Automatic Request Submission
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
      if (!token) {
        Notify.error("Session Expired", "Please log in again.");
        return;
      }

      const decoded = jwtDecode(token);
      const email = decoded.email || decoded.sub || decoded.preferred_username;

      await AuthServ.registerSeller({ email });

      // ✅ Update Local State
      setUserRole("pendingseller");
      localStorage.setItem("role", "pendingseller");

      Notify.success(
        "Application Submitted",
        "We are reviewing your request. You will be notified once approved."
      );

    } catch (error) {
      console.error("Seller request error:", error);
      Notify.error(error, "Request Failed");
    }
  };

  const handleLogin = () => {
    navigate("/seller-check");
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="start-selling-page">
          <div className="loading-spinner">Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="start-selling-page">
        
        {/* ======================================= */}
        {/* 1. HERO SECTION */}
        {/* ======================================= */}
        <section className="sp-hero">
          <div className="sp-hero-bg-glow"></div>
          <div className="sp-start-selling-wrapper start-selling-wrapper">
            <div className="sp-hero-grid">
              
              <div className="sp-hero-content">
                <span className="sp-badge">
                  <IconRocket size={16} /> Empowering Local Businesses
                </span>
                <h1 className="sp-title">Turn Your Local Business Into a Powerful Online Store</h1>
                <p className="sp-subtitle">
                  Sell groceries, vegetables, fruits, meat, milk, and more through Nearzon with real-time orders, delivery support, secure payouts, and powerful seller tools.
                </p>
                <div className="sp-hero-actions">
                  {!isLoggedIn ? (
                    <>
                      <button className="sp-btn-primary" onClick={handleLogin}>
                        Start Selling <IconArrowRight size={20} />
                      </button>
                      <button className="sp-btn-secondary" onClick={() => document.getElementById("features").scrollIntoView({behavior: "smooth"})}>
                        Explore Features
                      </button>
                    </>
                  ) : (
                    <button className="sp-btn-primary" onClick={() => document.getElementById("apply").scrollIntoView({behavior: "smooth"})}>
                      Go to Application <IconArrowRight size={20} />
                    </button>
                  )}
                </div>
              </div>

              <div className="sp-hero-visual">
                {/* CSS Animated Dashboard Mockup */}
                <div className="sp-mockup-main">
                  <div className="sp-mock-header">
                    <div className="line"></div>
                    <div className="circle"></div>
                  </div>
                  <div className="sp-mock-grid">
                    <div className="sp-mock-box"><div className="line1"></div><div className="line2"></div></div>
                    <div className="sp-mock-box"><div className="line1"></div><div className="line2"></div></div>
                  </div>
                  <div className="sp-mock-graph">
                    <div className="sp-mock-bar"></div><div className="sp-mock-bar"></div>
                    <div className="sp-mock-bar"></div><div className="sp-mock-bar"></div>
                    <div className="sp-mock-bar"></div>
                  </div>
                </div>
                
                <div className="sp-mockup-card-1">
                  <span style={{fontSize: "12px", color: "#64748b", fontWeight: "600"}}>LIVE ORDERS</span>
                  <div style={{fontSize: "24px", fontWeight: "800", color: "#0f172a", marginTop: "4px"}}>14 New</div>
                  <div style={{fontSize: "12px", color: "#10b981", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px"}}>
                    <span style={{width:"8px", height:"8px", borderRadius:"50%", background:"#10b981"}}></span> Assigning Delivery
                  </div>
                </div>

                <div className="sp-mockup-card-2">
                  <span style={{fontSize: "12px", opacity: 0.9, fontWeight: "600"}}>TODAY'S EARNINGS</span>
                  <div style={{fontSize: "28px", fontWeight: "800", marginTop: "4px"}}>₹12,450</div>
                  <div style={{fontSize: "12px", opacity: 0.9, marginTop: "8px", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "8px"}}>
                    Payout processing tomorrow
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 2. TRUST STATS SECTION */}
        {/* ======================================= */}
        <section className="sp-stats-section">
          <div className="start-selling-wrapper">
            <div className="sp-stats-grid">
              <div>
                <div className="sp-stat-num">500+</div>
                <div className="sp-stat-label">Active Sellers</div>
              </div>
              <div>
                <div className="sp-stat-num">10K+</div>
                <div className="sp-stat-label">Orders Delivered</div>
              </div>
              <div>
                <div className="sp-stat-num">40+</div>
                <div className="sp-stat-label">Cities Covered</div>
              </div>
              <div>
                <div className="sp-stat-num">24/7</div>
                <div className="sp-stat-label">Delivery Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* AUTH APPLICATION STATE (For logged in users) */}
        {/* ======================================= */}
        {isLoggedIn && (
          <section id="apply" style={{paddingTop: '60px'}}>
            <div className="start-selling-wrapper">
              <div className="sp-auth-flow-card">
                {userRole === "pendingseller" ? (
                  <div style={{ textAlign: "center" }}>
                    <div style={{display: "inline-flex", padding: "16px", background: "#fef3c7", color: "#d97706", borderRadius: "50%", marginBottom: "24px"}}>
                      <IconClockPlay size={40} />
                    </div>
                    <h2 className="sp-title" style={{fontSize: "28px", marginBottom: "16px"}}>Application Under Review</h2>
                    <p className="sp-subtitle" style={{marginBottom: "24px"}}>
                      Thank you for applying! Our team is reviewing your shop details. You will receive an email notification once approved.
                    </p>
                    <button className="sp-btn-secondary" disabled style={{margin: "0 auto", cursor: "not-allowed", background: "#f8fafc"}}>
                      Status: Pending Review
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <div style={{display: "inline-flex", padding: "16px", background: "#ecfdf5", color: "#10b981", borderRadius: "50%", marginBottom: "24px"}}>
                      <IconBuildingStore size={40} />
                    </div>
                    <h2 className="sp-title" style={{fontSize: "28px", marginBottom: "16px"}}>Ready to Launch Your Shop?</h2>
                    <p className="sp-subtitle" style={{marginBottom: "32px"}}>
                      You are logged in as {jwtDecode(localStorage.getItem("token")).email || "User"}. Click below to instantly submit your seller application with zero paperwork.
                    </p>
                    <GlobalButton action="apply" onClick={handleApplyNow} className="sp-btn-primary" style={{margin: "0 auto", padding: "0 40px"}}>
                      Apply Now to Sell
                    </GlobalButton>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ======================================= */}
        {/* 3. WHY SELL ON NEARZON (FEATURES) */}
        {/* ======================================= */}
        <section id="features" className="sp-features-section">
          <div className="start-selling-wrapper">
            <h2 className="sp-title sp-section-title">Everything you need to succeed</h2>
            <p className="sp-subtitle sp-section-subtitle">
              We provide the technology, delivery network, and customer base. You just provide the great products.
            </p>
            
            <div className="sp-features-grid">
              <div className="sp-feature-card">
                <div className="sp-feature-icon"><IconDeviceMobileMessage size={28} /></div>
                <h3>Real-time Orders</h3>
                <p>Accept, prepare, and dispatch orders in real-time. Instantly notify customers of order status.</p>
              </div>
              <div className="sp-feature-card">
                <div className="sp-feature-icon"><IconTruckDelivery size={28} /></div>
                <h3>Smart Delivery</h3>
                <p>Don't worry about logistics. Our system automatically assigns the nearest delivery partner to your store.</p>
              </div>
              <div className="sp-feature-card">
                <div className="sp-feature-icon"><IconShieldCheck size={28} /></div>
                <h3>Secure Payments</h3>
                <p>Fraud protection built-in. Accept online payments safely and handle Cash on Delivery with zero hassle.</p>
              </div>
              <div className="sp-feature-card">
                <div className="sp-feature-icon"><IconWallet size={28} /></div>
                <h3>Fast Payouts</h3>
                <p>Your earnings are tracked meticulously. Get automated direct bank transfers on a reliable schedule.</p>
              </div>
              <div className="sp-feature-card">
                <div className="sp-feature-icon"><IconBuildingStore size={28} /></div>
                <h3>Shop Control</h3>
                <p>Go on break, set vacation mode, or temporarily close your shop with a single click. You're in charge.</p>
              </div>
              <div className="sp-feature-card">
                <div className="sp-feature-icon"><IconChartBar size={28} /></div>
                <h3>Insights & Growth</h3>
                <p>View detailed sales reports, identify top-selling items, and create custom promotions to boost revenue.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 4. HOW IT WORKS TIMELINE */}
        {/* ======================================= */}
        <section className="sp-timeline-section">
          <div className="start-selling-wrapper" style={{textAlign: 'center'}}>
            <h2 className="sp-title sp-section-title">How it works</h2>
            <p className="sp-subtitle sp-section-subtitle">Start earning in four simple steps</p>
            
            <div className="sp-timeline-grid">
              <div className="sp-timeline-line"></div>
              
              <div className="sp-timeline-step">
                <div className="sp-timeline-number">1</div>
                <h4>Register Shop</h4>
                <p>Create your account and complete basic verification.</p>
              </div>
              
              <div className="sp-timeline-step">
                <div className="sp-timeline-number">2</div>
                <h4>Add Inventory</h4>
                <p>List your products, set prices, and manage stock.</p>
              </div>
              
              <div className="sp-timeline-step">
                <div className="sp-timeline-number">3</div>
                <h4>Receive Orders</h4>
                <p>Accept orders and pack them for our delivery partners.</p>
              </div>
              
              <div className="sp-timeline-step">
                <div className="sp-timeline-number">4</div>
                <h4>Get Paid</h4>
                <p>Receive regular, secure payouts directly to your bank.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 5. SHOP MANAGEMENT SPLIT SECTION */}
        {/* ======================================= */}
        <section className="sp-split-section">
          <div className="start-selling-wrapper">
            <div className="sp-split-grid">
              <div className="sp-split-content">
                <h2>Total Control Over Your Shop</h2>
                <p>Life happens. Whether you need a short break or are going on a vacation, our platform adapts to your schedule.</p>
                <ul className="sp-feature-list">
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>One-click Shop Open / Close</span></li>
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Temporary Break Mode (Auto-reopen)</span></li>
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Long-term Vacation Scheduling</span></li>
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Real-time Inventory Toggling</span></li>
                </ul>
              </div>
              <div className="sp-split-visual">
                <div className="sp-visual-card" style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#f8fafc', borderRadius: '12px'}}>
                    <div>
                      <div style={{fontWeight: '700', fontSize: '16px', color: '#0f172a'}}>Shop Status</div>
                      <div style={{fontSize: '13px', color: '#64748b'}}>Currently accepting orders</div>
                    </div>
                    <div style={{background: '#10b981', padding: '6px 16px', borderRadius: '99px', color: 'white', fontWeight: '700', fontSize: '13px'}}>OPEN</div>
                  </div>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#fff7ed', borderRadius: '12px', border: '1px solid #ffedd5'}}>
                    <div>
                      <div style={{fontWeight: '700', fontSize: '16px', color: '#ea580c'}}>Take a Break</div>
                      <div style={{fontSize: '13px', color: '#c2410c'}}>Pause orders for 30 mins</div>
                    </div>
                    <div style={{color: '#ea580c', fontWeight: '700'}}>ACTIVATE</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 6. DELIVERY & PAYOUT SPLIT SECTION */}
        {/* ======================================= */}
        <section className="sp-split-section" style={{background: '#f8fafc'}}>
          <div className="start-selling-wrapper">
            <div className="sp-split-grid reversed">
              <div className="sp-split-content">
                <h2>Effortless Delivery & Earnings</h2>
                <p>We manage the logistics so you can focus on your business. Track orders live and watch your revenue grow transparently.</p>
                <ul className="sp-feature-list">
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Automated Delivery Partner Assignment</span></li>
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Live Order Tracking Map</span></li>
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Detailed Earnings Dashboard</span></li>
                  <li><IconCheck size={20} className="sp-check-icon" /> <span>Automated Commission Deductions</span></li>
                </ul>
              </div>
              <div className="sp-split-visual">
                <div className="sp-visual-card">
                  <table className="sp-mock-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>#NZ-8492</td>
                        <td>₹850</td>
                        <td><span className="sp-status-badge sp-status-delivered">Delivered</span></td>
                      </tr>
                      <tr>
                        <td>#NZ-8493</td>
                        <td>₹1,200</td>
                        <td><span className="sp-status-badge sp-status-pending">On the way</span></td>
                      </tr>
                      <tr>
                        <td>#NZ-8494</td>
                        <td>₹340</td>
                        <td><span className="sp-status-badge sp-status-pending">Packing</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 7. TESTIMONIAL SECTION */}
        {/* ======================================= */}
        <section className="sp-testimonials">
          <div className="start-selling-wrapper">
            <h2 className="sp-title sp-section-title" style={{textAlign: 'center'}}>Trusted by Local Partners</h2>
            <p className="sp-subtitle sp-section-subtitle" style={{textAlign: 'center'}}>Join hundreds of business owners growing their revenue every day.</p>
            
            <div className="sp-test-grid">
              <div className="sp-test-card">
                <div className="sp-test-stars">★★★★★</div>
                <p className="sp-test-quote">"Since joining Nearzon, my daily orders have doubled. The delivery partners arrive on time, and the app makes managing stock so incredibly easy."</p>
                <div className="sp-test-author">
                  <div className="sp-test-avatar">RK</div>
                  <div>
                    <span className="sp-test-name">Ramesh Kumar</span>
                    <span className="sp-test-shop">Ramesh Grocery Store</span>
                  </div>
                </div>
              </div>
              
              <div className="sp-test-card">
                <div className="sp-test-stars">★★★★★</div>
                <p className="sp-test-quote">"The payout system is fully transparent. I never have to worry about missing payments. Plus, the temporary break feature is a lifesaver during rush hours!"</p>
                <div className="sp-test-author">
                  <div className="sp-test-avatar">SA</div>
                  <div>
                    <span className="sp-test-name">Sara Ali</span>
                    <span className="sp-test-shop">Fresh Veggies Mart</span>
                  </div>
                </div>
              </div>

              <div className="sp-test-card">
                <div className="sp-test-stars">★★★★★</div>
                <p className="sp-test-quote">"Setting up my shop took less than 10 minutes. The dashboard is modern and shows me exactly what my top selling items are."</p>
                <div className="sp-test-author">
                  <div className="sp-test-avatar">MD</div>
                  <div>
                    <span className="sp-test-name">Manoj Dairy</span>
                    <span className="sp-test-shop">City Milk Traders</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 8. FAQ SECTION */}
        {/* ======================================= */}
        <section className="sp-faq">
          <div className="start-selling-wrapper">
            <h2 className="sp-title sp-section-title" style={{textAlign: 'center'}}>Frequently Asked Questions</h2>
            
            <div className="sp-faq-grid">
              <div className="sp-faq-item">
                <div className="sp-faq-q">How fast can I start selling?</div>
                <div className="sp-faq-a">Most applications are reviewed within 24 hours. Once your shop is verified and approved by our admin team, you can immediately start adding inventory and accepting orders.</div>
              </div>
              
              <div className="sp-faq-item">
                <div className="sp-faq-q">Do I need my own delivery staff?</div>
                <div className="sp-faq-a">No! Nearzon automatically assigns our trusted delivery partners to pick up orders from your shop and deliver them to the customer. You just focus on preparing the order.</div>
              </div>
              
              <div className="sp-faq-item">
                <div className="sp-faq-q">How do payouts work?</div>
                <div className="sp-faq-a">Earnings from both online payments and Cash on Delivery orders are calculated on your dashboard. Payouts are processed on a regular, automated schedule directly to your registered bank account.</div>
              </div>
              
              <div className="sp-faq-item">
                <div className="sp-faq-q">What if I need to close my shop for a day?</div>
                <div className="sp-faq-a">You have full control. You can use the "Vacation Mode" to close your shop for multiple days, or use the "Take a Break" feature to pause orders temporarily during busy hours.</div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================= */}
        {/* 9. FINAL CTA SECTION */}
        {/* ======================================= */}
        <section className="sp-cta">
          <div className="start-selling-wrapper">
            <div className="sp-cta-card">
              <div className="sp-cta-glow"></div>
              <h2>Ready to grow your business?</h2>
              <p>Join the thousands of local sellers already scaling their revenue with Nearzon's powerful marketplace tools.</p>
              <div className="sp-cta-actions">
                {!isLoggedIn ? (
                  <button className="sp-btn-primary" onClick={handleLogin}>
                    Become a Seller Today <IconArrowRight size={20} />
                  </button>
                ) : (
                  <button className="sp-btn-primary" onClick={() => document.getElementById("apply").scrollIntoView({behavior: "smooth"})}>
                    Complete Application <IconArrowRight size={20} />
                  </button>
                )}
                <button className="sp-btn-secondary" style={{background: 'rgba(255,255,255,0.1)', color: 'white', borderColor: 'rgba(255,255,255,0.2)'}}>
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default StartSelling;

